import { useCallback } from 'react'
import { attemptRefresh } from './authRefresh'

export class ApiError extends Error {
  readonly code: string
  readonly field: string | undefined
  readonly dependents: unknown[] | undefined
  readonly status: number

  constructor(
    status: number,
    body: { code: string; message: string; field?: string; dependents?: unknown[] }
  ) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.code = body.code
    this.field = body.field
    this.dependents = body.dependents
  }
}

export interface ApiFetchOptions {
  method?: string
  body?: BodyInit | null
  headers?: HeadersInit
  mode?: RequestMode
  cache?: RequestCache
  redirect?: RequestRedirect
  referrer?: string
  referrerPolicy?: ReferrerPolicy
  integrity?: string
  keepalive?: boolean
  signal?: AbortSignal
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export async function apiFetch<T>(
  path: string,
  options?: ApiFetchOptions,
  _isRetryAfterRefresh = false
): Promise<T> {
  const requestId = generateRequestId()

  const timeout = AbortSignal.timeout(30_000)
  const signal =
    options?.signal != null ? AbortSignal.any([options.signal, timeout]) : timeout

  const headers = new Headers(options?.headers)
  if (!headers.has('Content-Type') && options?.body != null && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  headers.set('X-Request-Id', requestId)

  const response = await fetch(path, {
    method: options?.method,
    body: options?.body,
    credentials: 'include',
    mode: options?.mode,
    cache: options?.cache,
    redirect: options?.redirect,
    referrer: options?.referrer,
    referrerPolicy: options?.referrerPolicy,
    integrity: options?.integrity,
    keepalive: options?.keepalive,
    headers,
    signal,
  })

  const responseRequestId = response.headers.get('X-Request-Id')
  if (responseRequestId && responseRequestId !== requestId) {
    console.debug('[api] req-id out:', requestId, 'in:', responseRequestId)
  }

  if (!response.ok) {
    let body: { code: string; message: string; field?: string; dependents?: unknown[] }
    try {
      body = (await response.json()) as typeof body
    } catch {
      body = { code: 'network.error', message: `HTTP ${response.status}` }
    }
    if (response.status === 401 && body.code === 'auth.unauthorized' && !_isRetryAfterRefresh) {
      // auth.refresh_invalid / auth.refresh_unavailable are only ever returned by
      // /refresh itself (never by this generic branch, which only matches
      // auth.unauthorized) — so this can't loop into refreshing a refresh attempt.
      const refreshed = await attemptRefresh()
      if (refreshed) {
        return apiFetch<T>(path, options, true)
      }
    }
    if (response.status === 401 && body.code === 'auth.unauthorized') {
      window.dispatchEvent(new CustomEvent('auth:session-expired'))
    }
    throw new ApiError(response.status, body)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json() as Promise<T>
}

export function useApiFetch() {
  return useCallback(
    <T>(path: string, options?: ApiFetchOptions) => apiFetch<T>(path, options),
    []
  )
}
